import React, { lazy, Suspense, useLayoutEffect } from 'react';
import useSubscription from '../hooks/useSubscription';
import LoadingPage from '../pages/LoadingPage';
import { LicenseValidatorProps } from './types';

// Lazy loading Components
const RestrictedAccess = lazy(() => import('../pages/RestrictedAccess'));
const NoAccessStakeholder = lazy(() => import('../pages/NoAccessStakeholder'));
const ExpiredLicense = lazy(() => import('../components/ExpiredLicense'));
const StartTrialContent = lazy(() => import('../components/purchase/StartTrialContent'));

function LicenseValidator({
  children,
  enableHeader,
  disableHeader,
  onBuyNowClick,
  webTheme,
  pageState,
}: LicenseValidatorProps) {
  const subscriptionQuery = useSubscription();

  const isLoading = subscriptionQuery.isLoading || !subscriptionQuery.isSuccess;
  const isStakeholder = subscriptionQuery.isSuccess && subscriptionQuery.data.isStakeholder;
  const isNoSubscription = subscriptionQuery.isSuccess && subscriptionQuery.data.isNoSubscription;
  const hasRestrictedBillingAccess =
    subscriptionQuery.isSuccess && subscriptionQuery.data.isRestrictedBillingAccess;
  const hasExpiredOrInactiveLicense =
    subscriptionQuery.isSuccess &&
    !hasRestrictedBillingAccess &&
    (!subscriptionQuery.data.active || subscriptionQuery.data.expired);
  const hasValidAccess =
    subscriptionQuery.isSuccess &&
    !isStakeholder &&
    !isNoSubscription &&
    !hasRestrictedBillingAccess &&
    subscriptionQuery.data.active &&
    !subscriptionQuery.data.expired;

  // Header should show for:
  // - Expired/Inactive licenses (so they can see the expired message and buy)
  // - Restricted billing access (so they can navigate)
  // - Valid access (normal usage)
  const shouldShowHeader =
    hasExpiredOrInactiveLicense || hasRestrictedBillingAccess || hasValidAccess;

  // Update header state in useLayoutEffect to avoid setState during render
  // useLayoutEffect fires synchronously after DOM mutations but before paint
  useLayoutEffect(() => {
    // Ignore check if not on App (data loader, beta page)
    if (pageState !== 'app') return;

    if (shouldShowHeader) {
      enableHeader();
    } else {
      disableHeader();
    }
  }, [shouldShowHeader, enableHeader, disableHeader, pageState]);

  if (isLoading) {
    return <LoadingPage />;
  }

  if (isStakeholder) {
    return (
      <Suspense fallback={null}>
        <NoAccessStakeholder theme={webTheme} />
      </Suspense>
    );
  }

  if (isNoSubscription) {
    return (
      <Suspense fallback={null}>
        <StartTrialContent
          getStarted={subscriptionQuery.startTrial}
          subscription={subscriptionQuery.subscriptionClass}
        />
      </Suspense>
    );
  }

  if (hasRestrictedBillingAccess) {
    return (
      <Suspense fallback={null}>
        <RestrictedAccess />
      </Suspense>
    );
  }

  if (hasExpiredOrInactiveLicense) {
    return (
      <Suspense fallback={null}>
        <ExpiredLicense buy={onBuyNowClick} subscription={subscriptionQuery.subscriptionClass} />
      </Suspense>
    );
  }

  return children;
}

export default LicenseValidator;
